/**
 * Discrepancy validation for administrative recovery quantities.
 * Mirrors operator submission rules: any delivered ≠ planned requires a reason.
 */

export type RecoveryVerdict = 'APPROVE' | 'REJECT';

export const RECOVERY_VERDICTS = ['APPROVE', 'REJECT'] as const;

export interface RecoveryFormatEvidenceRow {
  formatId: number;
  planned: number;
  delivered: number;
  discrepancyReason?: string | null;
}

export interface RecoveryDiscrepancyValidation {
  valid: boolean;
  hasDiscrepancy: boolean;
  missingFormatIds: number[];
  formatRows: RecoveryFormatEvidenceRow[];
}

export function isRecoveryVerdict(value: string): value is RecoveryVerdict {
  return (RECOVERY_VERDICTS as readonly string[]).includes(value);
}

/** Tracker store row — on-behalf completion is for incomplete visits only, not finished stores. */
export function isStoreEligibleForAdministrativeRecovery(store: {
  status: string;
  totalCampaigns: number;
  doneCount: number;
  partialCount: number;
  pendingCount: number;
  issueCount: number;
}): boolean {
  if (store.totalCampaigns <= 0) return false;
  if (store.pendingCount > 0 || store.issueCount > 0) return true;
  if (store.status === 'DONE') return false;
  const completedCampaigns = store.doneCount + store.partialCount;
  if (completedCampaigns >= store.totalCampaigns && store.pendingCount === 0 && store.issueCount === 0) {
    return false;
  }
  return (
    store.status === 'PENDING' ||
    store.status === 'ACTIVE' ||
    store.status === 'PARTIAL' ||
    store.status === 'ISSUE' ||
    store.status === 'ORPHAN'
  );
}

export const STORE_COMPLETE_TASK_STATUSES_FOR_RECOVERY = [
  'operator_complete',
  'admin_complete',
  'approved',
  'verified',
  'locked',
] as const;

/** Task was sent back to the operator after an admin reset — recovery override must not apply. */
export const TASK_STATUSES_REOPENED_AFTER_RESET = [
  'pending',
  'sent_to_operator',
  'sent_to_operator_mobile',
  'sent_to_operator_pdf',
  'ready_for_operator',
] as const;

export interface StoreRecoveryEligibility {
  eligible: boolean;
  reason: string | null;
}

export function evaluateStoreRecoveryEligibility(taskStatuses: string[]): StoreRecoveryEligibility {
  const active = taskStatuses.filter((s) => s !== 'archived' && s !== 'recalled');
  if (active.length === 0) {
    return {
      eligible: false,
      reason: 'No active tasks for this store and operator in this cycle.',
    };
  }

  const hasIncompleteTask = active.some(
    (status) => !(STORE_COMPLETE_TASK_STATUSES_FOR_RECOVERY as readonly string[]).includes(status),
  );

  if (!hasIncompleteTask) {
    return {
      eligible: false,
      reason:
        'All tasks at this store are already complete. Completing on behalf of the operator is only for outstanding work — use Evidence to review operator submissions.',
    };
  }

  return { eligible: true, reason: null };
}

export function isTaskClosedByAdministrativeRecovery(task: {
  status: string;
  resolvedByOverrideId: number | null;
}): boolean {
  if ((TASK_STATUSES_REOPENED_AFTER_RESET as readonly string[]).includes(task.status)) {
    return false;
  }
  return (
    task.resolvedByOverrideId != null ||
    task.status === 'admin_complete' ||
    task.status === 'admin_rejected'
  );
}

export function buildRecoveryFormatEvidence(
  planFormats: Array<{ formatId: number; planned: number }>,
  deliveredByFormatId: Record<number, number>,
  discrepancyReasons: Record<number, string>,
): RecoveryFormatEvidenceRow[] {
  return planFormats.map((row) => ({
    formatId: row.formatId,
    planned: row.planned,
    delivered: deliveredByFormatId[row.formatId] ?? row.planned,
    discrepancyReason: discrepancyReasons[row.formatId]?.trim() || null,
  }));
}

export function validateRecoveryFormatDiscrepancies(
  formatRows: RecoveryFormatEvidenceRow[],
): RecoveryDiscrepancyValidation {
  const missingFormatIds: number[] = [];

  for (const row of formatRows) {
    if (row.delivered !== row.planned) {
      if (!row.discrepancyReason?.trim()) {
        missingFormatIds.push(row.formatId);
      }
    }
  }

  const hasDiscrepancy = formatRows.some((row) => row.delivered !== row.planned);

  return {
    valid: missingFormatIds.length === 0,
    hasDiscrepancy,
    missingFormatIds,
    formatRows,
  };
}
